// =====================================================================
// Blocos que podem NÃO EXISTIR no equipamento concreto.
//
// Nasceu na Etapa 1 (diagnóstico) e passou a valer também na Etapa 2, porque o
// problema é o mesmo nas duas: numa chapa elétrica de 2 platens, pedir resposta
// para o sistema a gás e para o Platen CENTRAL é ruído — e marcar tudo N/A na
// mão custa dezenas de cliques por certificação.
//
// A detecção é pelo NOME, não por uma coluna no banco. Sistemas, medições e
// testes já carregam a posição no próprio nome ("Platen CENTRAL · Aquecimento",
// "VÁLVULA 11 · Bico e difusor"), e criar uma coluna de agrupamento só para
// isto seria peso desnecessário. Em troca, a convenção de nome vira contrato:
// quem montar um template novo precisa segui-la.
//
// DUAS convenções convivem, uma por geração de equipamento:
//
//   1) POSIÇÃO (Chapa, 07/2026) — "Platen <ESQUERDO|CENTRAL|DIREITO> · <item>".
//      A quantidade vem da coluna dtbCertRelatorio.QTD_PLATENS. O ESQUERDO é o
//      sempre-presente; com 2 platens existem ESQUERDO e DIREITO; o CENTRAL é o
//      primeiro a faltar.
//
//   2) ÍNDICE (Bebidas, 09/2026) — "<EIXO> <n> · <item>", com EIXO em
//      SABOR/SISTEMA/TORRE/VÁLVULA e n começando em 1. A quantidade vem da
//      tabela dtbCertRelatorioQuantidade (um eixo por linha). Some quem passa
//      da quantidade declarada: 8 sabores no template, 3 informados ⇒ os
//      blocos 4 a 8 vão a N/A e saem do Índice de Saúde.
//
// A convenção 2 nasceu numérica de propósito: o Refrigerante tem três níveis
// (sistema → torre → válvula) e nomear posição para doze válvulas não tem
// vocabulário — "VÁLVULA 11" tem.
//
// Sem a informação (tipo de energia, nº de platens ou o eixo não declarado),
// NÃO PRESUME NADA (devolve false): esconder item por falta de informação é
// pior que mostrar item demais.
// =====================================================================

export const TIPOS_ENERGIA = ['Elétrica', 'Gás natural', 'GLP'];

// Eixos repetíveis por índice. A chave é o que vai na coluna EIXO da
// dtbCertRelatorioQuantidade — sem acento, para não depender de como o nome
// foi digitado no construtor de versões.
export const EIXOS_REPETIVEIS = ['SABOR', 'SISTEMA', 'TORRE', 'VALVULA'];

// Tira acento para casar "VÁLVULA" (nome do bloco) com 'VALVULA' (eixo).
const semAcento = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '');

// ⚠️ O \d+ logo depois do eixo é o que impede falso positivo: "Sistema de
//    água" e "Sistema Aquecimento - Resistência Cuba 3" são nomes reais de
//    sistema em templates publicados e NÃO podem casar aqui.
const RE_EIXO_INDICE = new RegExp(`^(${EIXOS_REPETIVEIS.join('|')})\\s+(\\d+)\\b`, 'i');

// `nome` serve para sistema, medição ou teste — todos seguem a mesma convenção.
//
// contexto: { tipoEnergia, qtdPlatens, quantidades }
//   quantidades — mapa EIXO → nº declarado (ex.: { TORRE: 2, VALVULA: 8 }).
//   Eixo fora do mapa (ou sem valor) = não declarado ⇒ não esconde nada.
export function itemNaoExiste(nome, contexto = {}) {
  const { tipoEnergia, qtdPlatens, quantidades } = contexto;
  const texto = String(nome || '');

  // Gás: some em equipamento elétrico. \b evita casar com palavra que apenas
  // contenha as letras (o nome vem de texto livre digitado no construtor).
  if (/\bg[áa]s\b/i.test(texto)) return tipoEnergia === 'Elétrica';

  const m = texto.match(/^Platen\s+(ESQUERDO|CENTRAL|DIREITO)\b/i);
  if (m) {
    const qtd = Number(qtdPlatens);
    if (!qtd) return false;
    const posicao = m[1].toUpperCase();
    if (qtd === 1) return posicao !== 'ESQUERDO';
    if (qtd === 2) return posicao === 'CENTRAL';
    return false;
  }

  const mi = semAcento(texto).match(RE_EIXO_INDICE);
  if (mi) {
    const eixo = mi[1].toUpperCase();
    const indice = Number(mi[2]);
    const declarado = Number(quantidades?.[eixo]);
    if (!declarado) return false;      // eixo não declarado ⇒ não presume
    return indice > declarado;
  }

  return false;
}

// Posições de platen que não existem — usado nos avisos da tela.
export function platensAusentes(tipoEnergia, qtdPlatens) {
  return ['CENTRAL', 'DIREITO'].filter((p) => itemNaoExiste(`Platen ${p} · x`, { tipoEnergia, qtdPlatens }));
}

// Rótulo de cada eixo no cabeçalho ("Quantidade de ___") e o maior índice que
// o eixo pode ter — o teto sai do PRÓPRIO template, não de uma constante:
// ver eixosDoTemplate abaixo.
export const ROTULO_EIXO = {
  SABOR: 'sabores/linhas',
  SISTEMA: 'sistemas',
  TORRE: 'torres',
  VALVULA: 'válvulas',
};

// Eixos cujo bloco ganha um NOME, e o que se pergunta em cada um.
//
// Vem direto do papel: "LINHA/SABOR ___ — __________" e "VÁLVULA ___ —
// PRODUTO: __________". Torre e sistema são só numerados no checklist, e
// oferecer um campo de nome para eles seria inventar preenchimento.
//
// ⚠️ É o que faz o relatório dizer "VÁLVULA 7 (Coca-Cola) · Vazão — Desgaste"
//    em vez de "VÁLVULA 7 · Vazão — Desgaste". Quem lê o certificado é o
//    gerente da loja, e a ação que ele toma depende de saber QUAL válvula.
export const EIXOS_COM_ROTULO = {
  SABOR: 'Sabor/linha',
  VALVULA: 'Produto',
};

// Chave do rótulo de um bloco, como o backend grava e devolve ("VALVULA:7").
export const chaveRotulo = (eixo, indice) => `${eixo}:${indice}`;

// Qual bloco repetível um nome representa: "VÁLVULA 7 · Vazão" → { VALVULA, 7 }.
// Devolve null para nome que não é bloco repetível (a maioria).
export function eixoDoNome(nome) {
  const m = semAcento(nome).match(RE_EIXO_INDICE);
  if (!m) return null;
  return { eixo: m[1].toUpperCase(), indice: Number(m[2]) };
}

// Quais eixos repetíveis ESTA versão do template usa, e até que índice.
// Derivado dos nomes da própria estrutura — de propósito, para não somar uma
// coluna de configuração no construtor de versões: quem carrega o template já
// escreveu "VÁLVULA 12 · ..." e isso basta.
//
// Devolve { VALVULA: 12, TORRE: 4, ... } — o valor é o TETO declarado, que a
// tela usa como máximo do campo de quantidade (pedir 14 válvulas num template
// que só tem 12 geraria bloco sem onde responder).
//
// `estrutura` = { sistemas: [{ NOME, componentes: [...] }], medicoes: [...], testes: [...] }
export function eixosDoTemplate(estrutura) {
  const nomes = [
    ...(estrutura?.sistemas || []).map((s) => s.NOME),
    ...(estrutura?.medicoes || []).map((m) => m.MEDICAO),
    ...(estrutura?.testes || []).map((t) => t.TESTE),
  ];
  const teto = {};
  for (const nome of nomes) {
    const m = semAcento(nome).match(RE_EIXO_INDICE);
    if (!m) continue;
    const eixo = m[1].toUpperCase();
    const indice = Number(m[2]);
    if (!teto[eixo] || indice > teto[eixo]) teto[eixo] = indice;
  }
  // Ordem estável para a tela não trocar os campos de lugar entre renders.
  return EIXOS_REPETIVEIS.filter((e) => teto[e]).reduce((acc, e) => ({ ...acc, [e]: teto[e] }), {});
}
